import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Upload, Search, Filter, Download, Eye, History, Check, X } from 'lucide-react'
import { format } from 'date-fns'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { DOCUMENT_TYPE_LABELS, PARCELAS } from '@/lib/constants'
import StatusBadge from '@/components/shared/StatusBadge'
import DocumentUpload from './DocumentUpload'
import type { Document, DocumentTypeEnum, DocumentStatus } from '@/types'

// =============================================================================
// DocumentManager Component
// =============================================================================

interface DocFilters {
  type:      DocumentTypeEnum | ''
  status:    DocumentStatus   | ''
  parcel:    number           | null
  search:    string
}

export default function DocumentManager() {
  const { canEdit, isSupervisor } = useAuth()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [filters, setFilters]       = useState<DocFilters>({
    type: '', status: '', parcel: null, search: '',
  })
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 20

  // ---------------------------------------------------------------------------
  // Fetch documents
  // ---------------------------------------------------------------------------
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['documents', filters, page],
    queryFn: async () => {
      if (isDemoMode) {
        const DEMO_DOCS = [
          { id: 'd1', filename: 'PPI_Zanjas_MT_Parc11_v2.pdf', document_type: 'PPI', status: 'RECHAZADO', file_size: 1240000, storage_path: '', notes: 'Falta firma supervisor', uploaded_by: 'demo-user-001', activity_instance_id: 'inst-1-5', parcel_id: 'parc-11', created_at: '2026-03-26T14:30:00Z', updated_at: '2026-03-26T14:30:00Z', deleted_at: null, uploader: { full_name: 'Mecano Trackers SL' }, parcel: { parcel_number: 11, display_name: 'Parcela 11' }, activity_instance: { family: { name: 'Zanjas MT' } } },
          { id: 'd2', filename: 'Checklist_Hincado_Parc06.xlsx', document_type: 'CHECKLIST', status: 'PENDIENTE', file_size: 85000, storage_path: '', notes: null, uploaded_by: 'demo-user-002', activity_instance_id: 'inst-0-1', parcel_id: 'parc-06', created_at: '2026-03-25T09:00:00Z', updated_at: '2026-03-25T09:00:00Z', deleted_at: null, uploader: { full_name: 'Civil Navabuena UTE' }, parcel: { parcel_number: 6, display_name: 'Parcela 6' }, activity_instance: { family: { name: 'Hincado' } } },
          { id: 'd3', filename: 'Fotografia_Tracker_NVS-TR-0042.jpg', document_type: 'FOTO', status: 'APROBADO', file_size: 3200000, storage_path: '', notes: null, uploaded_by: 'demo-user-001', activity_instance_id: 'inst-2-2', parcel_id: 'parc-03', created_at: '2026-03-24T11:00:00Z', updated_at: '2026-03-24T11:00:00Z', deleted_at: null, uploader: { full_name: 'Mecano Trackers SL' }, parcel: { parcel_number: 3, display_name: 'Parcela 3' }, activity_instance: { family: { name: 'Montaje de tracker' } } },
          { id: 'd4', filename: 'PPI_Conexionado_BT_Parc18.pdf', document_type: 'PPI', status: 'PENDIENTE', file_size: 980000, storage_path: '', notes: null, uploaded_by: 'demo-user-003', activity_instance_id: 'inst-7-4', parcel_id: 'parc-18', created_at: '2026-03-27T16:00:00Z', updated_at: '2026-03-27T16:00:00Z', deleted_at: null, uploader: { full_name: 'Electra Obras SA' }, parcel: { parcel_number: 18, display_name: 'Parcela 18' }, activity_instance: { family: { name: 'Conexionado' } } },
          { id: 'd5', filename: 'Cert_Calidad_Modulos_Parc02.pdf', document_type: 'CERTIFICADO', status: 'APROBADO', file_size: 450000, storage_path: '', notes: null, uploaded_by: 'demo-user-001', activity_instance_id: 'inst-1-3', parcel_id: 'parc-02', created_at: '2026-03-20T10:00:00Z', updated_at: '2026-03-20T10:00:00Z', deleted_at: null, uploader: { full_name: 'Mecano Trackers SL' }, parcel: { parcel_number: 2, display_name: 'Parcela 2' }, activity_instance: { family: { name: 'Montaje de módulos' } } },
        ]
        let items = DEMO_DOCS as unknown as Document[]
        if (filters.status) items = items.filter((d: unknown) => (d as { status: string }).status === filters.status)
        if (filters.search) items = items.filter((d: unknown) => (d as { filename: string }).filename.toLowerCase().includes(filters.search.toLowerCase()))
        return { items, total: items.length }
      }
      let query = supabase
        .from('documents')
        .select(`
          *,
          uploader:user_profiles!uploaded_by(full_name),
          parcel:parcels(parcel_number, display_name),
          activity_instance:activity_instances(
            family:activity_families(name)
          )
        `, { count: 'exact' })
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (filters.type)   query = query.eq('document_type', filters.type)
      if (filters.status) query = query.eq('status', filters.status)
      if (filters.parcel) {
        // Filter through parcel join
      }
      if (filters.search) {
        query = query.ilike('filename', `%${filters.search}%`)
      }

      const { data, count, error } = await query
      if (error) throw error
      return { items: (data ?? []) as Document[], total: count ?? 0 }
    },
    staleTime: 30_000,
  })

  const documents = data?.items ?? []
  const total     = data?.total ?? 0

  const handleDownload = async (doc: Document) => {
    const { data: urlData } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.storage_path, 60) // 60s TTL

    if (urlData?.signedUrl) {
      window.open(urlData.signedUrl, '_blank')
    }
  }

  const docTypes: { value: DocumentTypeEnum | ''; label: string }[] = [
    { value: '', label: 'Todos los tipos' },
    ...Object.entries(DOCUMENT_TYPE_LABELS).map(([v, l]) => ({
      value: v as DocumentTypeEnum,
      label: l,
    })),
  ]

  const docStatuses: { value: DocumentStatus | ''; label: string }[] = [
    { value: '',            label: 'Todos los estados' },
    { value: 'PENDIENTE',   label: 'Pendiente' },
    { value: 'EN_REVISION', label: 'En revisión' },
    { value: 'APROBADO',    label: 'Aprobado' },
    { value: 'RECHAZADO',   label: 'Rechazado' },
  ]

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar documento..."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            className="input-base pl-9 text-sm"
          />
        </div>

        {/* Type filter */}
        <select
          value={filters.type}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value as DocumentTypeEnum | '' }))}
          className="input-base text-sm w-auto"
        >
          {docTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {/* Status filter */}
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as DocumentStatus | '' }))}
          className="input-base text-sm w-auto"
        >
          {docStatuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* Parcel filter */}
        <select
          value={filters.parcel ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, parcel: e.target.value ? parseInt(e.target.value) : null }))}
          className="input-base text-sm w-auto"
        >
          <option value="">Todas las parcelas</option>
          {PARCELAS.map((p) => (
            <option key={p.number} value={p.number}>{p.displayName}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setUploadOpen(true)}
              className="btn-primary text-sm"
            >
              <Upload className="w-4 h-4" />
              Subir documento
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden flex-1 flex flex-col">
        <div className="overflow-x-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <table className="table-base">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th>Parcela</th>
                  <th>Actividad</th>
                  <th>Versión</th>
                  <th>Estado</th>
                  <th>Subido por</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-sm text-gray-400">
                      No se encontraron documentos
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => {
                    const uploader = doc.uploader as { full_name: string } | null
                    const parcel   = doc.parcel   as { parcel_number: number; display_name: string } | null
                    const activity = doc.activity_instance as { family: { name: string } } | null

                    return (
                      <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                        {/* Filename */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 max-w-[200px]">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${
                              doc.document_type === 'PPI'       ? 'bg-blue-500' :
                              doc.document_type === 'CHECKLIST' ? 'bg-green-500' :
                              doc.document_type === 'FOTO'      ? 'bg-yellow-500' :
                              doc.document_type === 'PLANO'     ? 'bg-purple-500' :
                              'bg-gray-400'
                            }`} />
                            <span className="text-xs font-medium text-gray-800 truncate">
                              {doc.filename}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600">
                            {DOCUMENT_TYPE_LABELS[doc.document_type] ?? doc.document_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {parcel?.display_name ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          <span className="truncate max-w-[120px] block">
                            {activity?.family?.name ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-center text-gray-600">
                          v{doc.current_version}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={doc.status} type="document" size="sm" dot />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {uploader?.full_name ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {format(new Date(doc.created_at), 'dd/MM/yy')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDownload(doc)}
                              className="p-1 rounded text-gray-400 hover:text-brand-600 hover:bg-brand-50"
                              title="Descargar"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                              title="Ver versiones"
                            >
                              <History className="w-3.5 h-3.5" />
                            </button>
                            {isSupervisor && doc.status === 'PENDIENTE' && (
                              <>
                                <button
                                  className="p-1 rounded text-gray-400 hover:text-green-600 hover:bg-green-50"
                                  title="Aprobar"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  title="Rechazar"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500">
            {total > 0
              ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} de ${total} documentos`
              : 'Sin documentos'
            }
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white"
            >
              Anterior
            </button>
            <button
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
              className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {/* Upload modal */}
      {uploadOpen && (
        <DocumentUpload
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setUploadOpen(false); refetch() }}
        />
      )}
    </div>
  )
}
